import { cn } from '../ui/utils';
import { scaleSegments, scoreToPercent } from './scaleGeometry';
import { bandStyle, type OrganBand } from './organHealth';

/**
 * Horizontal severity scale, Critical → Healthy, with a marker at the score.
 *
 * Display-only: no clinical math happens here. Every position — segment edges,
 * label centres, marker — comes from `scaleGeometry.ts`, which derives all of them
 * from the backend's band table. The three used to be positioned by three
 * different formulas, which is why the labels sat under the wrong colours.
 */
export function BandScale({
  score,
  band,
  className,
}: {
  score: number | null;
  band: OrganBand;
  className?: string;
}) {
  const hasMarker = typeof score === 'number' && Number.isFinite(score);
  const clamped = hasMarker ? Math.max(0, Math.min(100, score as number)) : 0;
  const markerPct = scoreToPercent(clamped);
  const segments = scaleSegments();

  return (
    <div className={cn('w-full', className)}>
      <div className="relative">
        {/* Track: one segment per band, widths proportional to their real ranges. */}
        <div className="relative h-2.5 w-full overflow-hidden rounded-full" role="presentation">
          {segments.map((seg) => (
            <div
              key={seg.band}
              className={cn(bandStyle(seg.band).fill, 'absolute inset-y-0')}
              style={{ left: `${seg.startPct}%`, width: `${seg.widthPct}%` }}
            />
          ))}
        </div>

        {/* Marker — same mapping as the segments, so it can never disagree. */}
        {hasMarker && (
          <div
            className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${markerPct}%` }}
            aria-hidden="true"
          >
            <div className="h-5 w-5 rounded-full border-2 border-background bg-foreground shadow-md ring-1 ring-black/10" />
          </div>
        )}
      </div>

      {/* Axis labels, each centred on ITS OWN segment rather than evenly spaced.
          translate-x-[-50%] centres the label on the segment's midpoint; the
          outermost two are nudged inward by the flex alignment so they cannot
          overflow the track at narrow widths. */}
      {/* The five labels do not fit in a narrow card: measured at a 285px
          track, MILD and HEALTHY overlapped by 5px and rendered as the single
          word "MILDHEALTHY". Below the threshold the three interior labels are
          dropped and the endpoints kept, which is what the axis is actually
          for -- which way is worse, and where this score sits between them.
          Nothing is lost: the score's own band is named in the chip beside it.

          A CONTAINER query, not a viewport one: these cards sit in a
          multi-column grid, so a card can be 285px wide on a 1400px screen and
          a viewport breakpoint would miss it entirely. */}
      <div className="band-scale-axis relative mt-1.5 h-3.5">
        {segments.map((seg, i) => {
          const isFirst = i === 0;
          const isLast = i === segments.length - 1;
          return (
            <span
              key={seg.band}
              data-axis-label={isFirst || isLast ? 'end' : 'mid'}
              className={cn(
                'absolute top-0 whitespace-nowrap text-[10px] font-medium uppercase tracking-wide text-text-tertiary/70',
                isFirst && 'translate-x-0',
                isLast && '-translate-x-full',
                !isFirst && !isLast && '-translate-x-1/2',
              )}
              style={{ left: isFirst ? '0%' : isLast ? '100%' : `${seg.centerPct}%` }}
            >
              {bandStyle(seg.band).label}
            </span>
          );
        })}
      </div>

      <div className="sr-only">
        {hasMarker
          ? `Score ${clamped} of 100, ${bandStyle(band).label} band.`
          : 'Score not available.'}
      </div>
    </div>
  );
}
